/**
 * @file Badge.jsx
 * @description Status indicator / tag component.
 *
 * @param {'solid'|'outline'|'subtle'|'dot'} [variant='subtle']
 * @param {'gray'|'blue'|'green'|'yellow'|'orange'|'red'|'purple'} [color='gray']
 * @param {'sm'|'md'} [size='md']
 * @param {boolean} [removable=false] - Show X button for filter tags
 * @param {Function} [onRemove] - Called when X is clicked
 * @param {string} [className]
 * @param {React.ReactNode} children
 */
import React from 'react';

const colorMap = {
  gray:   { solid: '#6B7280', subtle: '#F3F4F6', text: '#374151', border: '#D1D5DB', dot: '#9CA3AF' },
  blue:   { solid: '#2563EB', subtle: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE', dot: '#3B82F6' },
  green:  { solid: '#16A34A', subtle: '#F0FDF4', text: '#15803D', border: '#BBF7D0', dot: '#22C55E' },
  yellow: { solid: '#D97706', subtle: '#FFFBEB', text: '#B45309', border: '#FDE68A', dot: '#F59E0B' },
  orange: { solid: '#EA580C', subtle: '#FFF7ED', text: '#C2410C', border: '#FED7AA', dot: '#F97316' },
  red:    { solid: '#DC2626', subtle: '#FEF2F2', text: '#B91C1C', border: '#FECACA', dot: '#EF4444' },
  purple: { solid: '#7C3AED', subtle: '#F5F3FF', text: '#6D28D9', border: '#DDD6FE', dot: '#8B5CF6' },
};

/** Inspection status presets */
const STATUS_PRESETS = {
  'Draft':        { color: 'gray',   variant: 'subtle' },
  'In Progress':  { color: 'blue',   variant: 'subtle' },
  'Completed':    { color: 'green',  variant: 'subtle' },
  'Delivered':    { color: 'purple', variant: 'subtle' },
  'Paid':         { color: 'green',  variant: 'solid'  },
};

function Badge({
  variant = 'subtle',
  color = 'gray',
  size = 'md',
  removable = false,
  onRemove,
  children,
  className = '',
  style: userStyle,
  ...rest
}) {
  // Auto-detect status preset
  const preset = typeof children === 'string' ? STATUS_PRESETS[children] : null;
  const resolvedColor = preset?.color || color;
  const resolvedVariant = preset?.variant || variant;

  const colors = colorMap[resolvedColor] || colorMap.gray;

  const sizeStyles = {
    sm: {
      padding: resolvedVariant === 'dot' ? '0' : 'var(--space-0_5) var(--space-2)',
      fontSize: 'var(--text-overline)',
      gap: 'var(--space-1)',
    },
    md: {
      padding: resolvedVariant === 'dot' ? '0' : 'var(--space-1) var(--space-2_5)',
      fontSize: 'var(--text-caption)',
      gap: 'var(--space-1_5)',
    },
  };

  const variantStyles = {
    solid: {
      backgroundColor: colors.solid,
      color: '#FFFFFF',
      border: 'none',
    },
    outline: {
      backgroundColor: 'transparent',
      color: colors.text,
      border: `1.5px solid ${colors.border}`,
    },
    subtle: {
      backgroundColor: colors.subtle,
      color: colors.text,
      border: 'none',
    },
    dot: {
      backgroundColor: 'transparent',
      color: 'var(--text-primary)',
      border: 'none',
    },
  };

  const currentSize = sizeStyles[size] || sizeStyles.md;
  const currentVariant = variantStyles[resolvedVariant] || variantStyles.subtle;

  const badgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: currentSize.gap,
    padding: currentSize.padding,
    fontSize: currentSize.fontSize,
    fontFamily: 'var(--font-family)',
    fontWeight: 'var(--weight-semibold)',
    lineHeight: 1.4,
    borderRadius: 'var(--radius-full)',
    whiteSpace: 'nowrap',
    ...currentVariant,
    ...userStyle,
  };

  return (
    <span
      className={`ib-badge ib-badge--${resolvedVariant} ib-badge--${resolvedColor} ${className}`}
      style={badgeStyle}
      {...rest}
    >
      {resolvedVariant === 'dot' && (
        <span
          aria-hidden="true"
          style={{
            width: size === 'sm' ? '6px' : '8px',
            height: size === 'sm' ? '6px' : '8px',
            borderRadius: 'var(--radius-full)',
            backgroundColor: colors.dot,
            flexShrink: 0,
          }}
        />
      )}

      {children}

      {removable && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (onRemove) onRemove();
          }}
          aria-label="Remove"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '14px',
            height: '14px',
            padding: 0,
            border: 'none',
            background: 'none',
            color: 'inherit',
            cursor: 'pointer',
            borderRadius: 'var(--radius-full)',
            opacity: 0.6,
            flexShrink: 0,
            marginLeft: 'var(--space-0_5)',
          }}
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </span>
  );
}

export default Badge;
